<template>
  <v-container style="max-width: 1000px; min-height: 500px; padding-bottom: 100px;">
    <LeagueTimeSettings @refresh="refresh" />
    <div>
      <Table
        @rowClicked="handleRowClicked"
        class="mt-5"
        emptyText="Ei aikoja"
        :loading="loading_runs"
        :data="tableData"
        :subtitle="'Avaa pelaajan ajat klikkaamalla riviä'"
      />
    </div>
    <v-dialog
      v-model="showingRunsForRow"
      max-width="1000"
      :fullscreen="$vuetify.breakpoint.mobile"
    >
      <v-card v-if="showingRunsForRow"
        style="min-height: 600px; height: 100%; max-width: 100vw; overflow-y: scroll;"
      >
        <IndividualRunsList
          @refresh="refresh"
          @close="showingRunsForRow = false"
          :is_play="type === 'field'"
        />
        <div v-if="$vuetify.breakpoint.mobile" style="height: 90px;"></div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import LeagueTimeSettings from '@/components/leaguetimes/LeagueTimeSettings.vue';
import Table from '@/components/table/Table.vue';
import IndividualRunsList from '@/components/leaguetimes/IndividualRunsList.vue';

export default {
  name: 'LeagueTimes',
  props: {
    type: {
      type: String,
      required: true,
      validator: value => ['advancement', 'field'].includes(value)
    }
  },
  watch: {
    type: {
      immediate: true,
      handler(newType) {
        this.SET_IS_PLAY(newType === 'field')
        this.refresh()
      }
    }
  },
  created() {
    this.refresh()
  },
  components: { LeagueTimeSettings, Table, IndividualRunsList },
  data: () => ({
    cellWidth: 100,
    showingRunsForRow: null
  }),
  computed: {
    ...mapGetters('user', [
      'currentTeam',
      'leagueId',
      'currentLeagueName'
    ]),
    ...mapGetters('leaguetimes', [
      'runs',
      'loading_runs',
      'base'
    ]),
    tableData() {
      if (this.type === 'field') {
        return {
          headers: [
            { text: ' ', value: 'index', center: true, width: '20px', nowrap: true },
            { text: 'Pelaaja', value: 'player_name', center: false, width: '170px', locked: true },
            {
              text: 'Suoritus ka.',
              value: 'avg_ball_at_base_time',
              center: true,
              bold: true,
              width: '120px',
              tooltip: 'Syöttö nousee -> Pallo pesässä keskiarvo',
            },
            {
              text: 'Määrä',
              value: 'num_of_runs',
              center: true,
              width: this.cellWidth + 'px',
              tooltip: 'Mitattuja aikoja',
            },
          ],
          items: this.runs.map((play, index) => {
            return [
              { key: 'index', value: index + 1 },
              { key: 'player_name', value: play.player_name },
              { key: 'avg_ball_at_base_time', value: play.avg_ball_at_base_time ? play.avg_ball_at_base_time?.toFixed(2) : '-' },
              { key: 'num_of_runs', value: play.num_of_runs },
            ]
          })
        }
      }

      return {
        headers: [
          { text: ' ', value: 'index', center: true, width: '40px', bold: true, nowrap: true },
          { text: 'Pelaaja', value: 'player_name', center: false, width: '170px', locked: true },
          {
            text: 'Ka.',
            value: 'avg_eteneminen_time',
            bold: true,
            center: true,
            width: this.cellWidth + 'px',
            tooltip: 'Etenemisten keskiarvo',
          },
          {
            text: 'Lähtö ka.',
            value: 'avg_lahto_time',
            center: true,
            width: this.cellWidth + 'px',
            tooltip: 'Lähtöjen keskiarvo (Syöttö nousee -> lähtö)',
          },
          {
            text: 'Raaka ka.',
            value: 'avg_raaka_time',
            center: true,
            width: this.cellWidth + 'px',
            tooltip: 'Raakojen juoksuaikojen keskiarvo (lähtö -> pesässä)',
          },
          {
            text: 'Piikki',
            value: 'min_run_time',
            center: true,
            width: this.cellWidth + 'px',
            tooltip: 'Paras eteneminen',
          },
          {
            text: 'Etäisyys',
            value: 'avg_distance_from_base',
            center: true,
            width: this.cellWidth + 'px',
            tooltip: 'Lähdön etäisyyden keskiarvo',
            bases: [1, 2],
            leagues: ['msu', 'nsu']
          },
          {
            text: 'Määrä',
            value: 'num_of_runs',
            label: true,
            labelColor: v => v >= 3 ? 'primary' : 'error',
            center: true,
            width: this.cellWidth + 'px',
            tooltip: 'Mitattuja aikoja',
          },
        ],
        items: this.runs.map((run, index) => {
          return [
            { key: 'index', value: index + 1 },
            { key: 'image', value: run.image_url },
            { key: 'player_name', value: run.player_name },
            { key: 'avg_eteneminen_time', value: run.avg_eteneminen_time?.toFixed(2) },
            { key: 'avg_lahto_time', value: run.avg_lahto_time?.toFixed(2) },
            { key: 'avg_raaka_time', value: run.avg_raaka_time?.toFixed(2) },
            { key: 'min_run_time', value: run.min_run_time?.toFixed(2) },
            { key: 'avg_distance_from_base', value:  run.avg_distance_from_base ? `${run.avg_distance_from_base?.toFixed(1)} m` : '-' },
            { key: 'num_of_runs', value: run.num_of_runs },
          ]
        })
      }
    }
  },
  methods: {
    ...mapActions('leaguetimes', [
      'getRuns',
      'getPlayerRuns'
    ]),
    ...mapMutations('leaguetimes', [
      'SET_CURRENT_PLAYER',
      'SET_IS_PLAY'
    ]),
    handleRowClicked(rowIndex) {
      const row = this.runs[rowIndex]
      this.showingRunsForRow = row
      this.SET_CURRENT_PLAYER({
        id: row.player_id,
        name: row.player_name,
        img: row.picture_url
      })
      this.getPlayerRuns({ player_id: row.player_id, league_id: this.currentTeam.league_id })
    },
    refresh() {
      this.$nextTick(() => {
        this.getRuns({
          league_id: this.leagueId,
          league_name: this.currentLeagueName,
          isPlay: this.type === 'field'
        })
      })
    }
  }
}
</script>